import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  Truck,
  CreateTruckRequest,
  UpdateTruckRequest,
  TruckStatus,
  TrailerType,
  ContainerType,
} from "../../interfaces/truck.interface";
import TruckService from "../../services/Truck.service";
import { useLoader } from "../../contexts/LoaderContext";
import { useLoading } from "../../contexts/LoadingContext";
import { useAuth } from "../../contexts/AuthContext";
import SearchBar from "../../components/SearchBar/SearchBar";
import TableComponent, {
  TableColumn,
} from "../../components/TableComponent/TableComponent";
import StatsBoxContainer from "../../components/StatsBoxContainer/StatsBoxContainer";
import { showToast } from "../../components/Toaster";
import { SelectChangeEvent } from "@mui/material";
import { usePermissions, PERMISSIONS } from "../../hooks/usePermissions";
import TruckForm from "../../components/Trucks/TruckForm";
import ConfirmDialog from "../../components/ConfirmDialog/ConfirmDialog";
import { transformTruckNumber } from "../../utils/general";
import "./Trucks.scss";

interface TruckStats {
  totalTrucks: number;
  activeTrucks: number;
  inactiveTrucks: number;
  maintenanceTrucks: number;
}

function Trucks() {
  const { showLoader, hideLoader } = useLoader();
  const loader = useLoading();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const { userProfile } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [trucks, setTrucks] = useState<Truck[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchSuggestions, setSearchSuggestions] = useState<string[]>([]);
  const [trucksLoaded, setTrucksLoaded] = useState(false);
  const initialLoadDone = useRef(false);
  const isLoading = useRef(false);
  const [truckStats, setTruckStats] = useState<TruckStats>({
    totalTrucks: 0,
    activeTrucks: 0,
    inactiveTrucks: 0,
    maintenanceTrucks: 0,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [showTruckForm, setShowTruckForm] = useState(false);
  const [selectedTruck, setSelectedTruck] = useState<Truck | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [truckToDelete, setTruckToDelete] = useState<Truck | null>(null);

  const canManageTrucks = can(
    PERMISSIONS.CAN_CRUD_TRUCK_TRAILER_FOR_SELF_OR_TRANSPORTER
  );

  const fetchTrucks = useCallback(
    async (suppressLoader?: boolean, forceRefresh?: boolean) => {
      try {
        if (!initialLoadDone.current) {
          setTrucksLoaded(false);
        }

        if (!suppressLoader) {
          showLoader();
        } else {
          loader.setLoading(true);
        }

        setError(null);

        const response = await TruckService.getTrucks(
          {
            skip: (currentPage - 1) * pageSize,
            limit: pageSize,
            search: searchQuery,
          },
          userProfile
        );

        if (!response.error) {
          const trucksData: Truck[] = response.data.content;
          setTrucks(trucksData);

          // Calculate stats
          const stats: TruckStats = {
            totalTrucks: response.data.page.totalElements,
            activeTrucks: trucksData.filter(
              (t) => t.status === TruckStatus.ACTIVE
            ).length,
            inactiveTrucks: trucksData.filter(
              (t) => t.status === TruckStatus.INACTIVE
            ).length,
            maintenanceTrucks: trucksData.filter(
              (t) => t.status === TruckStatus.MAINTENANCE
            ).length,
          };
          setTruckStats(stats);
        } else {
          console.error("Error in fetching trucks:", response.message);
          setError(response.message || "Error fetching trucks");
        }
      } catch (err: any) {
        console.error("Error fetching data:", err);
        setError(err.message || "An error occurred while fetching data");
      } finally {
        setTrucksLoaded(true);
        initialLoadDone.current = true;

        setTimeout(() => {
          if (!suppressLoader) {
            hideLoader();
          } else {
            loader.setLoading(false);
          }
        }, 1000);
      }
    },
    [
      showLoader,
      hideLoader,
      loader,
      currentPage,
      pageSize,
      searchQuery,
      userProfile,
    ]
  );

  // Memoize table data transformation
  const tableData = useMemo(() => {
    return trucks.map((truck) => {
      try {
        return {
          id: truck.id,
          registrationNumber: transformTruckNumber(truck.registrationNumber),
          engineNumber: truck.engineNumber || "N/A",
          chasisNumber: truck.chasisNumber || "N/A",
          make: truck.make || "N/A",
          model: truck.model || "N/A",
          year: truck.year || "N/A",
          capacity:
            truck.capacity && truck.capacityUnit
              ? `${truck.capacity} ${truck.capacityUnit}`
              : "N/A",
          trailerType: (() => {
            if (!truck.trailerType) return "N/A";
            if (typeof truck.trailerType !== "string") return "N/A";
            if (truck.trailerType.trim() === "") return "N/A";
            return truck.trailerType.replace("_", " ");
          })(),
          containerType: truck.containerType || "N/A",
          status: truck.status || "N/A",
          insuranceExpiry: truck.insuranceExpiryDate
            ? new Date(truck.insuranceExpiryDate).toLocaleDateString()
            : "N/A",
          permitExpiry: truck.permitExpiryDate
            ? new Date(truck.permitExpiryDate).toLocaleDateString()
            : "N/A",
          fitnessExpiry: truck.fitnessExpiryDate
            ? new Date(truck.fitnessExpiryDate).toLocaleDateString()
            : "N/A",
          pucExpiry: truck.pucExpiryDate
            ? new Date(truck.pucExpiryDate).toLocaleDateString()
            : "N/A",
        };
      } catch (error) {
        console.error("Error processing truck:", truck, error);
        return {
          id: truck.id || "N/A",
          registrationNumber: "N/A",
          engineNumber: "N/A",
          chasisNumber: "N/A",
          make: "N/A",
          model: "N/A",
          year: "N/A",
          capacity: "N/A",
          trailerType: "N/A",
          containerType: "N/A",
          status: "N/A",
          insuranceExpiry: "N/A",
          permitExpiry: "N/A",
          fitnessExpiry: "N/A",
          pucExpiry: "N/A",
        };
      }
    });
  }, [trucks]);

  // Define table columns
  const columns: TableColumn[] = [
    {
      header: "Truck Number",
      accessor: "registrationNumber",
      sortable: true,
      width: "150px",
    },
    {
      header: "Engine Number",
      accessor: "engineNumber",
      sortable: true,
      width: "180px",
    },
    {
      header: "Chassis Number",
      accessor: "chasisNumber",
      sortable: true,
      width: "180px",
    },
    {
      header: "Make",
      accessor: "make",
      sortable: true,
      // cell: (value, row) => `${row.make}`,
      width: "200px",
    },
    {
      header: "Model",
      accessor: "model",
      sortable: true,
      // cell: (value, row) => `${row.model}`,
      width: "200px",
    },
    {
      header: "Year",
      accessor: "year",
      sortable: true,
      centerAlign: true,
      width: "80px",
    },
    {
      header: "Capacity",
      accessor: "capacity",
      sortable: true,
      centerAlign: true,
      width: "120px",
    },
    {
      header: "Trailer Type",
      accessor: "trailerType",
      sortable: true,
      width: "150px",
    },
    {
      header: "Container Type",
      accessor: "containerType",
      sortable: true,
      centerAlign: true,
      width: "120px",
    },
    {
      header: "Insurance Expiry",
      accessor: "insuranceExpiry",
      sortable: true,
      centerAlign: true,
      width: "130px",
    },
    {
      header: "Status",
      accessor: "status",
      sortable: true,
      centerAlign: true,
      cell: (value) => (
        <span className={`status-badge status-${value.toLowerCase()}`}>
          {value}
        </span>
      ),
      width: "120px",
    },
  ];

  // Table actions
  const tableActions = [
    {
      label: "Edit",
      callback: (row: any) => {
        const truck = trucks.find((item) => item.id === row.id);
        if (truck) {
          handleEditTruck(truck);
        }
      },
      isEnabled: () => canManageTrucks,
    },
    {
      label: "Delete",
      callback: (row: any) => {
        const truck: Truck = {
          id: row.id,
          registrationNumber: row.registrationNumber,
          model: row.model,
          make: row.make,
          year: row.year,
          capacity: 0,
          capacityUnit: "kg",
          fuelType: "Diesel",
          insuranceExpiryDate: Date.now(),
          permitExpiryDate: Date.now(),
          fitnessExpiryDate: Date.now(),
          pucExpiryDate: Date.now(),
          trailerType: row.trailerType as TrailerType,
          containerType: row.containerType as ContainerType,
          status: row.status as TruckStatus,
        };
        handleDeleteTruck(truck);
      },
      isEnabled: () => canManageTrucks,
    },
  ];

  useEffect(() => {
    fetchTrucks();
  }, [fetchTrucks]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (event: SelectChangeEvent<number>) => {
    setPageSize(Number(event.target.value));
    setCurrentPage(1);
  };

  const handleAddTruck = () => {
    setSelectedTruck(null);
    setShowTruckForm(true);
  };

  const handleEditTruck = (truck: Truck) => {
    setSelectedTruck(truck);
    setShowTruckForm(true);
  };

  const handleDeleteTruck = (truck: Truck) => {
    setTruckToDelete(truck);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (truckToDelete) {
      try {
        await TruckService.deleteTruck(truckToDelete.id!);
        showToast("Truck deleted successfully", "success");
        fetchTrucks(true, true);
        setDeleteDialogOpen(false);
        setTruckToDelete(null);
      } catch (error) {
        console.error("Error deleting truck:", error);
        showToast("Failed to delete truck", "error");
      }
    }
  };

  const handleTruckFormClose = () => {
    setShowTruckForm(false);
    setSelectedTruck(null);
  };

  const handleTruckFormSuccess = () => {
    fetchTrucks(true, true);
  };

  const statsData = [
    {
      name: "Total Trucks",
      value: truckStats.totalTrucks,
      valueColor: "#1062b9",
    },
    {
      name: "Active Trucks",
      value: truckStats.activeTrucks,
      valueColor: "#28a745",
    },
    {
      name: "Inactive Trucks",
      value: truckStats.inactiveTrucks,
      valueColor: "#dc3545",
    },
    {
      name: "Maintenance",
      value: truckStats.maintenanceTrucks,
      valueColor: "#ffc107",
    },
  ];

  if (error) {
    return (
      <div className="trucks-page">
        <div className="error-container">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={() => fetchTrucks()}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="trucks-page p-[20px]">
      <StatsBoxContainer stats={statsData} />

      <div className="trucks-toolbar">
        <SearchBar
          placeholder="Search trucks..."
          onSearch={handleSearch}
          showClearButton={true}
          suggestions={searchSuggestions}
          onSuggestionClick={(suggestion) => {
            setSearchQuery(suggestion);
            setCurrentPage(1);
          }}
        />
        <div className="trucks-toolbar-actions">
          {canManageTrucks && (
            <button
              type="button"
              className="primary_button"
              onClick={handleAddTruck}
            >
              Add Truck
            </button>
          )}
          <div className="flex items-center space-x-2">
            <button
              type="button"
              className={`secondary_button ${
                loader.isLoading ? "opacity-70" : ""
              }`}
              onClick={() => fetchTrucks(true, true)}
              disabled={loader.isLoading}
            >
              <div className="flex items-center">
                {loader.isLoading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Refreshing...
                  </>
                ) : (
                  "Refresh"
                )}
              </div>
            </button>
          </div>
        </div>
      </div>

      {trucksLoaded && !loader.isLoading && tableData.length === 0 ? (
        <div className="flex justify-center items-center">
          <p className="text-gray-500">No trucks found</p>
        </div>
      ) : (
        <div className="trucks-table">
          <TableComponent
            showPagination={true}
            columns={columns}
            data={tableData}
            searchQuery={searchQuery}
            showCheckboxes={false}
            uniqueAccessor="id"
            currentPage={currentPage}
            pageSize={pageSize}
            totalPages={Math.ceil(truckStats.totalTrucks / pageSize)}
            totalElements={truckStats.totalTrucks}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            enableClientSideFiltering={false}
            isServerSidePagination={true}
            actions={tableActions}
          />
        </div>
      )}

      {showTruckForm && (
        <TruckForm
          isOpen={showTruckForm}
          onClose={handleTruckFormClose}
          truck={selectedTruck}
          onSuccess={handleTruckFormSuccess}
          userProfile={userProfile}
        />
      )}

      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Truck"
        message={`Are you sure you want to delete truck ${truckToDelete?.registrationNumber}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmColor="error"
      />
    </div>
  );
}

export default Trucks;
